import {
  filters,
  FILTER_TYPE,
  API_INFINITY_VALUE,
} from '../../../../../constants';
import { dots } from '../../../../../functions';
import type { CustomCompanyFilter } from './types';
import { toSignificant } from '@/utilities/formatting';

export const generatePastFilters = (): CustomCompanyFilter[] => [
  // Earnings past 5 years
  {
    id: filters.pastFiveYearGrowth,
    label: 'Annual Earnings Growth',
    subLabel: '(Past 5 Year Annual Earnings Growth)',
    labels: {
      ...dots(75, { start: -25, step: 12.5 }),
      '-30': '-∞',
      '-25': '-25%',
      0: '0%',
      25: '25%',
      50: '50%',
      55: '∞',
    },
    defaultValue: `["${filters.pastFiveYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.pastFiveYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -30;
        } else if (value >= API_INFINITY_VALUE) {
          return 55;
        } else {
          return value;
        }
      }),
    min: -30,
    max: 55,
    type: FILTER_TYPE.RANGE,
    softMin: -25,
    softMax: 50,
    valueResolver: (range) =>
      range.map((value) =>
        value < -25
          ? API_INFINITY_VALUE * -1
          : value > 50
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // Revenue past 5 years
  {
    id: filters.past_revenue_growth5y,
    label: 'Annual Revenue Growth',
    subLabel: '(Past 5 Year Annual Revenue Growth)',
    labels: {
      ...dots(230, { start: -25, step: 25 }),
      '-30': '-∞',
      '-25': '-25%',
      0: '0%',
      50: '50%',
      100: '100%',
      200: '200%',
      205: '∞',
    },
    defaultValue: `["${filters.past_revenue_growth5y}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.past_revenue_growth5y}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -30;
        } else if (value >= API_INFINITY_VALUE) {
          return 205;
        } else {
          return value;
        }
      }),
    min: -30,
    max: 205,
    type: FILTER_TYPE.RANGE,
    softMin: -25,
    softMax: 200,
    valueResolver: (range) =>
      range.map((value) =>
        value < -25
          ? API_INFINITY_VALUE * -1
          : value > 200
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // Past Earnings 1 year
  {
    id: filters.pastOneYearGrowth,
    label: '1 Year Earnings Growth',
    subLabel: '(Last 1 Year Earnings Growth)',
    labels: {
      ...dots(100, { start: -50, step: 25 }),
      '-55': '-∞',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      55: '∞',
    },
    defaultValue: `["${filters.pastOneYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.pastOneYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -55;
        } else if (value >= API_INFINITY_VALUE) {
          return 55;
        } else {
          return value;
        }
      }),
    min: -55,
    max: 55,
    type: FILTER_TYPE.RANGE,
    softMin: -50,
    softMax: 50,
    valueResolver: (range) =>
      range.map((value) =>
        value < -50
          ? API_INFINITY_VALUE * -1
          : value > 50
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // Revenue last 1 year
  {
    id: filters.past_revenue_growth1y,
    label: '1 Year Revenue Growth',
    subLabel: '(Last 1 Year Revenue Growth)',
    labels: {
      ...dots(230, { start: -25, step: 25 }),
      '-30': '-∞',
      '-25': '-25%',
      0: '0%',
      50: '50%',
      100: '100%',
      200: '200%',
      205: '∞',
    },
    defaultValue: `["${filters.past_revenue_growth1y}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.past_revenue_growth1y}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -30;
        } else if (value >= API_INFINITY_VALUE) {
          return 205;
        } else {
          return value;
        }
      }),
    min: -30,
    max: 205,
    type: FILTER_TYPE.RANGE,
    softMin: -25,
    softMax: 200,
    valueResolver: (range) =>
      range.map((value) =>
        value < -25
          ? API_INFINITY_VALUE * -1
          : value > 200
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // ROE
  {
    id: filters.currentRoe,
    label: 'ROE Ratio',
    subLabel: '(Return on Equity)',
    labels: {
      ...dots(50, { step: 5 }),
      0: '0%',
      25: '25%',
      50: '50%',
      55: '∞',
    },
    min: 0,
    max: 55,
    defaultValue: `["${filters.currentRoe}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.currentRoe}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
  },
  // ROA
  {
    id: filters.currentRoa,
    label: 'ROA Ratio',
    subLabel: '(Return on Assets)',
    labels: {
      ...dots(50, { step: 5 }),
      0: '0%',
      25: '25%',
      50: '50%',
      55: '∞',
    },
    min: 0,
    max: 55,
    defaultValue: `["${filters.currentRoa}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.currentRoa}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // ROCE
  {
    id: filters.past_return_on_capital_employed,
    label: 'ROCE Ratio',
    subLabel: '(Return on Capital Employed)',
    labels: {
      ...dots(50, { step: 5 }),
      0: '0%',
      25: '25%',
      50: '50%',
      55: '∞',
    },
    min: 0,
    max: 55,
    defaultValue: `["${filters.past_return_on_capital_employed}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.past_return_on_capital_employed}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // Profit Margin
  {
    id: filters.past_net_income_margin,
    label: 'Profit Margin',
    subLabel: '(Ratio of Earnings to Revenue)',
    type: FILTER_TYPE.RANGE,
    labels: {
      ...dots(100, { step: 10 }),
      0: '0%',
      50: '50%',
      100: '100%',
    },
    min: 0,
    max: 100,
    defaultValue: `["${filters.past_net_income_margin}", "between", 0, 100]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.past_net_income_margin}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : `${max}%`}`,
    //return value as float as expected by API
    valueResolver: (range) =>
      range.map((value) => toSignificant(value / 100, 2)),
  },
  // EBITDA Margin
  {
    id: filters.past_ebitda_margin,
    label: 'Ebitda Margin',
    subLabel: '(Ratio of Ebitda to Revenue)',
    type: FILTER_TYPE.RANGE,
    labels: {
      ...dots(100, { step: 10 }),
      0: '0%',
      50: '50%',
      100: '100%',
    },
    min: 0,
    max: 100,
    defaultValue: `["${filters.past_ebitda_margin}", "between", 0, 100]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.past_ebitda_margin}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : `${max}%`}`,
    //return value as float as expected by API
    valueResolver: (range) =>
      range.map((value) => toSignificant(value / 100, 2)),
  },

  // EPS
  // Special case `onToggle` event
  // Untoggle `is_eps_less_than_zero` if `EPS` is toggled
  {
    id: filters.eps,
    label: 'EPS',
    subLabel: '(Earning per Share)',
    labels: {
      ...dots(100, { start: -100, step: 50 }),
      '-105': '-∞',
      '-100': '-100',
      '-50': '-50',
      0: '0',
      50: '50',
      100: '100',
      105: '∞',
    },
    min: -105,
    max: 105,
    softMin: -100,
    softMax: 100,
    defaultValue: `["${filters.eps}", "between", 50, 100]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.eps}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -105;
        } else if (value >= API_INFINITY_VALUE) {
          return 105;
        } else {
          return value;
        }
      }),
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      return range.map((value) =>
        value < -100
          ? API_INFINITY_VALUE * -1
          : value > 100
            ? API_INFINITY_VALUE
            : value
      );
    },
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min} and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min} and ${max}`;
      }
    },
  },
];
